import type { Board, BoardColumn, BoardLane, IssueCard } from '@tasks/contracts';

/**
 * Оптимистичное перемещение карточки внутри доски: применяется до ответа
 * сервера и откатывается при ошибке (UC-BRD-02 AC1).
 */
export function moveCardInBoard(board: Board, issueId: string, toStatusId: string): Board {
  const card = findCard(board, issueId);
  if (!card || card.statusId === toStatusId) {
    return board;
  }
  // Целевую колонку проверяем до удаления: иначе карточка потерялась бы.
  if (!board.columns.some((column) => column.status.id === toStatusId)) {
    return board;
  }
  const withoutCard = removeCardFromBoard(board, issueId);
  return insertCardIntoColumn(withoutCard, { ...card, statusId: toStatusId }, toStatusId);
}

export function findCard(board: Board, issueId: string): IssueCard | null {
  for (const column of board.columns) {
    for (const lane of column.lanes) {
      for (const issue of lane.issues) {
        if (issue.id === issueId) {
          return issue;
        }
      }
    }
  }
  return null;
}

function removeCardFromBoard(board: Board, issueId: string): Board {
  let removed = false;
  const columns = board.columns.map((column) => {
    let removedHere = 0;
    const lanes = column.lanes.map((lane) => {
      const issues = lane.issues.filter((issue) => {
        if (issue.id !== issueId) {
          return true;
        }
        removed = true;
        removedHere += 1;
        return false;
      });
      return { ...lane, issues, count: Math.max(0, lane.count - removedHere) };
    });
    return { ...column, lanes, issueCount: Math.max(0, column.issueCount - removedHere) };
  });
  return removed ? { ...board, columns, total: Math.max(0, board.total - 1) } : board;
}

export function insertCardIntoColumn(board: Board, card: IssueCard, statusId: string): Board {
  const index = board.columns.findIndex((column) => column.status.id === statusId);
  if (index < 0) {
    return board;
  }
  const columns = board.columns.map((column, columnIndex) => {
    if (columnIndex !== index || column.lanes.length === 0) {
      return column;
    }
    const [first, ...rest] = column.lanes;
    if (!first) {
      return column;
    }
    return {
      ...column,
      issueCount: column.issueCount + 1,
      lanes: [{ ...first, issues: [card, ...first.issues], count: first.count + 1 }, ...rest],
    };
  });
  return { ...board, columns, total: board.total + 1 };
}

/** Патч карточки во всех колонках и полосах. */
export function patchCardInBoard(board: Board, issueId: string, patch: Partial<IssueCard>): Board {
  let changed = false;
  const columns = board.columns.map((column) => ({
    ...column,
    lanes: column.lanes.map((lane) => ({
      ...lane,
      issues: lane.issues.map((issue) => {
        if (issue.id !== issueId) {
          return issue;
        }
        changed = true;
        return { ...issue, ...patch };
      }),
    })),
  }));
  return changed ? { ...board, columns } : board;
}

export function columnIssues(column: BoardColumn): readonly IssueCard[] {
  return column.lanes.flatMap((lane) => lane.issues);
}

export function laneIssues(lane: BoardLane): readonly IssueCard[] {
  return lane.issues;
}

/** `N/limit` с подсветкой при превышении (UC-BRD-06). */
export function isWipExceeded(column: BoardColumn): boolean {
  return column.wipLimit !== null && column.wipLimit > 0 && column.issueCount > column.wipLimit;
}

export type BoardFilters = {
  readonly swimlane: string;
  readonly sprintId: string | null;
  readonly assigneeId: string | null;
  readonly label: string | null;
  readonly q: string;
};

/** Ключ React Query для конкретного вида доски — из URL-параметров. */
export function boardFiltersFromParams(params: URLSearchParams): BoardFilters {
  return {
    swimlane: params.get('swimlane') ?? 'none',
    sprintId: params.get('sprintId'),
    assigneeId: params.get('assigneeId'),
    label: params.get('label'),
    q: params.get('q') ?? '',
  };
}
